import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import RoleCenter from "@/pages/RoleCenter";
import Projects from "@/pages/Projects";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import { Purchases, Sales } from "@/pages/Ledger";
import ProfitLoss from "@/pages/ProfitLoss";
import BalanceSheet from "@/pages/finance/BalanceSheet";
import Budgets from "@/pages/finance/Budgets";
import OpeningBalances from "@/pages/finance/OpeningBalances";
import GSTReports from "@/pages/reports/GSTReports";
import AMCContracts from "@/pages/sales/AMCContracts";
import MaterialsIntelligence from "@/pages/materials/MaterialsIntelligence";
import PurchaseGenius from "@/pages/purchases/PurchaseGenius";
import PurchaseAnalytics from "@/pages/purchases/PurchaseAnalytics";
import RABilling from "@/pages/billing/RABilling";
import ProjectPnL from "@/pages/reports/ProjectPnL";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import ProjectDetail from "@/pages/ProjectDetail";
import Aging from "@/pages/Aging";
import Recurring from "@/pages/Recurring";
import Meetings from "@/pages/Meetings";
import VendorPOs from "@/pages/VendorPOs";
import DaikinPOs from "@/pages/daikin-pos/DaikinPOs";
import Inventory from "@/pages/Inventory";
import Warehouses from "@/pages/warehouse/Warehouses";
import StockTransfers from "@/pages/warehouse/StockTransfers";
import MaterialMovement from "@/pages/warehouse/MaterialMovement";
import MaterialRequests from "@/pages/procurement/MaterialRequests";
import EquipmentRegistry from "@/pages/service/EquipmentRegistry";
import Snags from "@/pages/service/Snags";
import ServiceTickets from "@/pages/service/ServiceTickets";
import CommissioningReports from "@/pages/service/CommissioningReports";
import PPMSchedules from "@/pages/service/PPMSchedules";
import WarrantyTracker from "@/pages/service/WarrantyTracker";
import Csat from "@/pages/service/Csat";
import PublicFeedback from "@/pages/service/PublicFeedback";
import GoodsReceipt from "@/pages/warehouse/GoodsReceipt";
import GoodsReceiptsList from "@/pages/warehouse/GoodsReceiptsList";
import Approvals from "@/pages/Approvals";
import TwoFactor from "@/pages/security/TwoFactor";
import AuditLog from "@/pages/security/AuditLog";
import PermissionMatrix from "@/pages/security/PermissionMatrix";
import Quotations from "@/pages/sales/Quotations";
import PriceCatalog from "@/pages/sales/PriceCatalog";
import SalesOrders from "@/pages/sales/SalesOrders";
import ProformaInvoices from "@/pages/sales/ProformaInvoices";
import Payroll from "@/pages/hr/Payroll";
import Employees from "@/pages/hr/Employees";
import AttendanceRegister from "@/pages/hr/AttendanceRegister";
import PunchApp from "@/pages/punch/PunchApp";
import "@/App.css";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            {/* Public feedback link — no auth */}
            <Route path="/feedback/:token" element={<PublicFeedback />} />
            {/* Employee mobile punch — separate PIN login, no staff auth */}
            <Route path="/punch" element={<PunchApp />} />

            <Route path="/" element={<Protected><RoleCenter /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/recurring" element={<Protected><Recurring /></Protected>} />
            <Route path="/aging" element={<Protected><Aging /></Protected>} />
            <Route path="/payments" element={<Protected><Payments /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/purchases" element={<Protected><Purchases /></Protected>} />
            <Route path="/vendor-pos" element={<Protected><VendorPOs /></Protected>} />
            <Route path="/daikin-pos" element={<Protected><DaikinPOs /></Protected>} />
            <Route path="/sales" element={<Protected><Sales /></Protected>} />
            <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/warehouses" element={<Protected><Warehouses /></Protected>} />
            <Route path="/stock-transfers" element={<Protected><StockTransfers /></Protected>} />
            <Route path="/material-movement" element={<Protected><MaterialMovement /></Protected>} />
            <Route path="/goods-receipts" element={<Protected><GoodsReceiptsList /></Protected>} />
            <Route path="/goods-receipts/new" element={<Protected><GoodsReceipt /></Protected>} />
            <Route path="/material-requests" element={<Protected><MaterialRequests /></Protected>} />
            <Route path="/meetings" element={<Protected><Meetings /></Protected>} />
            <Route path="/profit" element={<Protected><ProfitLoss /></Protected>} />
            <Route path="/finance/balance-sheet" element={<Protected><BalanceSheet /></Protected>} />
            <Route path="/finance/budgets" element={<Protected><Budgets /></Protected>} />
            <Route path="/finance/opening-balances" element={<Protected><OpeningBalances /></Protected>} />
            <Route path="/reports/gst" element={<Protected><GSTReports /></Protected>} />
            <Route path="/amc-contracts" element={<Protected><AMCContracts /></Protected>} />
            <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
            <Route path="/sales-orders" element={<Protected><SalesOrders /></Protected>} />
            <Route path="/proforma-invoices" element={<Protected><ProformaInvoices /></Protected>} />
            <Route path="/payroll" element={<Protected><Payroll /></Protected>} />
            <Route path="/payroll/employees" element={<Protected><Employees /></Protected>} />
            <Route path="/payroll/attendance" element={<Protected><AttendanceRegister /></Protected>} />
            <Route path="/price-catalog" element={<Protected><PriceCatalog /></Protected>} />
            <Route path="/materials-intel" element={<Protected><MaterialsIntelligence /></Protected>} />
            <Route path="/purchase-genius" element={<Protected><PurchaseGenius /></Protected>} />
            <Route path="/purchase-analytics" element={<Protected><PurchaseAnalytics /></Protected>} />
            <Route path="/ra-billing" element={<Protected><RABilling /></Protected>} />
            <Route path="/project-pnl" element={<Protected><ProjectPnL /></Protected>} />
            <Route path="/users" element={<Protected><Users /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />

            {/* Service (SV) module */}
            <Route path="/service/equipment" element={<Protected><EquipmentRegistry /></Protected>} />
            <Route path="/service/snags" element={<Protected><Snags /></Protected>} />
            <Route path="/service/tickets" element={<Protected><ServiceTickets /></Protected>} />
            <Route path="/service/commissioning" element={<Protected><CommissioningReports /></Protected>} />
            <Route path="/service/ppm" element={<Protected><PPMSchedules /></Protected>} />
            <Route path="/service/warranty" element={<Protected><WarrantyTracker /></Protected>} />
            <Route path="/service/csat" element={<Protected><Csat /></Protected>} />

            {/* Governance & Security */}
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/security/2fa" element={<Protected><TwoFactor /></Protected>} />
            <Route path="/security/audit" element={<Protected><AuditLog /></Protected>} />
            <Route path="/security/permissions" element={<Protected><PermissionMatrix /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
